import React from "react";
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import { RelatorioStyles as styles } from "./styles";

interface BoletoPagoCaixaItem {
  nomeContribuinte: string;
  cpfContribuinte: string;
  dataEmissao: string;
  dataVencimento: string;
  dataPagamento: string;
  valorParcela: string;
  valorParcelaNumerico: number;
}

interface RelatorioPDFProps {
  data: BoletoPagoCaixaItem[];
  lotacao?: string;
  localPagamento?: string;
  dateRange: {
    inicio: string;
    fim: string;
  };
  totalRegistros: number;
  valorTotal: number;
}

const ITEMS_PER_PAGE = 17; // Número de registros por página, ajustável

const RelatorioPDF: React.FC<RelatorioPDFProps> = ({
  data,
  lotacao,
  localPagamento,
  dateRange,
  totalRegistros,
  valorTotal,
}) => {
  console.log(data.length);
  console.log(lotacao);
  console.log(localPagamento);
  console.log(dateRange);
  console.log(valorTotal);

  // Formatar valor monetário
  const formatCurrency = (value: number | string): string => {
    if (typeof value === "string") {
      if (value.includes("R$")) return value;
      // Remover possíveis formatações que possam estar no valor string
      const valorLimpo = value.replace(/[^\d,.]/g, "").replace(",", ".");
      const valorNumerico = parseFloat(valorLimpo);

      if (isNaN(valorNumerico)) {
        return `R$ ${value}`;
      }

      return `R$ ${valorNumerico.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    return `R$ ${value.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Número real de registros na tabela
  const registrosReais = data.length;

  // Calcular o valor total das parcelas
  const calcularValorTotal = (): number => {
    // Se o valorTotal já for fornecido e válido, usamos ele
    if (valorTotal && !isNaN(valorTotal) && valorTotal > 0) {
      return valorTotal;
    }

    // Caso contrário, calculamos baseado nos valores das parcelas
    return data.reduce((total, item) => {
      if (item.valorParcelaNumerico && !isNaN(item.valorParcelaNumerico)) {
        return total + item.valorParcelaNumerico;
      }

      if (item.valorParcela) {
        // Tentar extrair o valor numérico da string
        const valorLimpo = item.valorParcela
          .replace(/[^\d,.]/g, "")
          .replace(",", ".");
        const valorNumerico = parseFloat(valorLimpo);

        if (!isNaN(valorNumerico)) {
          return total + valorNumerico;
        }
      }

      return total;
    }, 0);
  };

  const valorTotalCalculado = calcularValorTotal();

  // Dividir os dados em páginas
  const pages = [];
  for (let i = 0; i < data.length; i += ITEMS_PER_PAGE) {
    pages.push(data.slice(i, i + ITEMS_PER_PAGE));
  }

  return (
    <Document>
      {pages.map((pageData, pageIndex) => (
        <Page
          key={pageIndex}
          style={styles.page}
          size="A4"
          orientation="landscape"
        >
          {/* Cabeçalho */}
          <View style={styles.header}>
            <View style={styles.logoContainer}>
              <Image
                src={require("../../../assets/logo-imagem.png")}
                style={styles.logo}
              />
            </View>
            <View style={styles.headerText}>
              <Text style={styles.institution}>
                Associação Beneficente Nossa Senhora da Conceição
              </Text>
              <Text style={styles.details}>
                Santa Casa de Misericórdia e Maternidade Nossa Senhora da
                Conceição
              </Text>
              <Text style={styles.details}>
                CNPJ 47.531.835/0001-31 Inscrição Estadual: isenta
              </Text>
              <Text style={styles.details}>
                Avenida Padre Rodolfo, 320 - Alto do Cruzeiro - 12530-000 -
                Cunha-SP
              </Text>
              <Text style={styles.title}>
                RELATÓRIO DE BOLETOS ENVIADOS POR REMESSA E PAGOS NO CAIXA
              </Text>
            </View>
          </View>

          {/* Tabela */}
          <View style={styles.table}>
            <View style={styles.row}>
              <Text style={styles.cellHeaderName}>Nome Contribuinte</Text>
              <Text style={styles.cellHeader}>CPF</Text>
              <Text style={styles.cellHeader}>Data de Emissão</Text>
              <Text style={styles.cellHeader}>Data de Vencimento</Text>
              <Text style={styles.cellHeader}>Data de Pagamento</Text>
              <Text style={styles.cellHeader}>Valor Parcela</Text>
            </View>

            {pageData.map((item, index) => (
              <View style={styles.row} key={`${pageIndex}-${index}`}>
                <Text style={styles.cellName}>
                  {item.nomeContribuinte.toUpperCase()}
                </Text>
                <Text style={styles.cell}>{item.cpfContribuinte}</Text>
                <Text style={styles.cell}>{item.dataEmissao}</Text>
                <Text style={styles.cell}>{item.dataVencimento}</Text>
                <Text style={styles.cell}>{item.dataPagamento}</Text>
                <Text style={styles.cell}>
                  {item.valorParcela
                    ? formatCurrency(item.valorParcela)
                    : item.valorParcelaNumerico
                    ? formatCurrency(item.valorParcelaNumerico)
                    : "R$ 0,00"}
                </Text>
              </View>
            ))}

            {/* Total apenas na última página */}
            {pageIndex === pages.length - 1 && (
              <>
                <View style={styles.row}>
                  <Text style={styles.cellName}>
                    TOTAL ({registrosReais} registro(s))
                  </Text>
                  <Text style={styles.cell}></Text>
                  <Text style={styles.cell}></Text>
                  <Text style={styles.cell}></Text>
                  <Text style={styles.cell}></Text>
                  <Text style={styles.cell}>
                    {formatCurrency(valorTotalCalculado)}
                  </Text>
                </View>
                {/* Filtros aplicados */}
                <View style={styles.filtersMainContainer}>
                  <View style={styles.filters}>
                    <View style={styles.filterContainer}>
                      <Text style={styles.filterText}>Lotação: </Text>
                      <Text style={styles.filterTextBold}>
                        {lotacao || "Todas"}
                      </Text>
                    </View>
                    <View style={styles.filterContainer}>
                      <Text style={styles.filterText}>
                        Local de Pagamento:{" "}
                      </Text>
                      <Text style={styles.filterTextBold}>
                        {localPagamento || "Todas"}
                      </Text>
                    </View>
                    <View style={styles.filterContainer}>
                      <Text style={styles.filterText}>Período: </Text>
                      <Text style={styles.filterTextBold}>
                        {dateRange.inicio} até {dateRange.fim}
                      </Text>
                    </View>
                  </View>
                </View>
              </>
            )}
          </View>

          {/* Rodapé */}
          <View style={styles.footer}>
            <Text>
              Data de emissão: {new Date().toLocaleDateString("pt-BR")}
            </Text>
            <Text
              render={({ pageNumber, totalPages }) =>
                `Página: ${pageNumber} de ${totalPages}`
              }
            />
            <Text>Desenvolvido por FATECOINS</Text>
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default RelatorioPDF;
